import { bootstrapSessionOutcome, getCurrentAccessToken } from '@jq4race/api-client';

import { getCurrentSession } from '../../../app/auth-state.js';
import { buildRealCheckinContext } from '../../checkin/infrastructure/build-real-checkin-context.js';
import { checkinCreateHandler } from '../../checkin/infrastructure/checkin-create.handler.js';
import { computeBackoffMs } from '../domain/backoff.js';
import { classifySyncError } from '../domain/classify-sync-error.js';
import { offlineDb } from '../infrastructure/dexie/offline-db.js';
import { DexieBlobStore } from '../infrastructure/dexie-blob-store.js';
import { DexieOutboxRepository } from '../infrastructure/dexie-outbox.repository.js';
import {
  recordSyncOutcome,
  reportIncidentPhotosDropped,
  reportRejectedItem,
} from '../infrastructure/observability.js';
import { cleanupSyncedBlobs } from './blob-cleanup.js';
import { createForegroundDrainer } from './foreground-drainer.js';
import { purgeOldOutboxRows } from './purge-old-outbox.js';

const repo = new DexieOutboxRepository(offlineDb);
const blobStore = new DexieBlobStore(offlineDb);

/**
 * foregroundDrainer — the app-wide singleton (coordinator finding #1). Both
 * `CheckinPage` (post-enqueue trigger) and
 * `useForegroundDrainerTriggers` (boot/online/periodic triggers, mounted
 * once in `_authenticated.tsx`) call `.tick()` on this SAME instance, so the
 * in-tab single-flight guard actually guards across every trigger source,
 * not just one.
 */
export const foregroundDrainer = createForegroundDrainer({
  repo,
  handler: checkinCreateHandler,
  // third adversarial review finding H1 — buildContext was silently
  // IGNORING the item it was handed, so buildRealCheckinContext had no way
  // to send the item's own X-Location-Id/offline headers on its
  // photo-upload-url requests. Now threaded through.
  // fourth adversarial review MEDIUM #2 — also threads `repo` through, so
  // buildRealCheckinContext can persist uploaded photo keys for reuse.
  buildContext: (item) => buildRealCheckinContext(blobStore, repo, item),
  classify: classifySyncError,
  computeBackoff: (attempts) => computeBackoffMs(attempts),
  now: () => new Date(),
  // second adversarial review LOW #9 — sweep synced blobs so IndexedDB
  // doesn't grow forever.
  cleanupSyncedBlobs: () => cleanupSyncedBlobs(offlineDb),
  // MEDIUM fix (adversarial review) — bounded outbox retention, same call
  // site as cleanupSyncedBlobs above.
  purgeOldOutbox: () => purgeOldOutboxRows(offlineDb),
  // third adversarial review finding M1 — only recover needs-auth items
  // when a real access token exists right now; a cached `identity` alone
  // (the offline-shell case) must never trigger a recovery attempt that
  // would just re-fail with another 401.
  hasSession: () => getCurrentAccessToken() !== null,
  // Finding #4 (shared-device adversarial review) — resolves the CURRENT
  // live session's identity from the same auth store `main.tsx`/
  // `_authenticated.tsx` read, never from the cached offline profile.
  getLiveIdentity: () => {
    const session = getCurrentSession();
    if (!session?.user.organizationId) return null;
    return { orgId: session.user.organizationId, capturedByUserId: session.user.id };
  },
  // Same finding — the exact cookie-based bootstrap main.tsx/
  // useOfflineShellRecovery already use; its own de-duplicated in-flight
  // promise (packages/api-client) means this never double-fires a refresh
  // that one of those callers happens to have started at the same time.
  ensureLiveSession: async () => {
    await bootstrapSessionOutcome();
  },
  // task 7.9 (design §9 observability) — the ONE composition-root wiring of
  // observability.ts's Sentry wrappers; foreground-drainer.ts itself never
  // imports @sentry/react.
  observeSyncOutcome: recordSyncOutcome,
  observeRejectedItem: reportRejectedItem,
  observeIncidentPhotosDropped: reportIncidentPhotosDropped,
});
